import React, { useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  Card,
  FilteredTable,
  ObjectName,
  ObjectOnline,
  MenuButton,
  IndeterminateCheckbox,
  Spinner,
  Tag,
} from 'components'
import ApiHelper from 'helpers/ApiHelper'
import { prefetchQuery } from 'helpers/QueryHelper'
import {
  ObjectActive,
  ObjectOnlineState,
  ObjectPublic,
} from 'components/shared/Objects'

const AdminGroupObjectsView = (props) => {
  //  --- Variables ---
  let history = useHistory()
  const { t } = useTranslation()
  const tableRef = useRef()
  const refreshRef = useRef()
  const groupId = props.match.params.id

  const { data: group } = useQuery(
    ['group', groupId],
    () => ApiHelper.showGroup(groupId),
    {
      refetchOnWindowFocus: false,
    }
  )

  const onRemoveAccessBulk = useCallback(async () => {
    const objectIds = tableRef.current
      .getSelectedRows()
      .map((r) => r.original.objectId)
    await toast.promise(ApiHelper.bulkRemoveGroupAccess(groupId, objectIds), {
      loading: 'Removing access...',
      success: 'Access rights updated.',
      error: 'Could not remove access rights.',
    })

    if (refreshRef.current) refreshRef.current()
  }, [tableRef, refreshRef, groupId])

  //  --- Response ---
  return (
    <div className="flex h-full flex-col">
      <GroupCard group={group} />

      <Card cardType="primary-content" className="my-4 h-full">
        <FilteredTable
          apiName={['indexGroupObjects', props.match.params.id]}
          dataFormatter={(data) =>
            data.map((row) => ({ ...row, overrideOnClick: [0] }))
          }
          dataApi={() => ApiHelper.showGroupObject(props.match.params.id)}
          queryProps={{
            refetchOnWindowFocus: false,
          }}
          tableRef={tableRef}
          refreshRef={refreshRef}
          filterCacheKey={['indexGroupObjects', props.match.params.id]}
          initialSort={{ id: 'name', desc: false }}
          placeholder={t('application:search')}
          removeFooter={true}
          scrollRestore={true}
          rowOnClick={(row) => {
            prefetchQuery(
              ['object', row.objectId, { accessLevel: 'access' }],
              row
            )
            history.push(`/objects/access/${row.objectId}`)
          }}
          tableHeaders={tableHeaders}
        >
          <MenuButton
            title={t('application:bulk')}
            options={[
              {
                key: 'rm_access',
                title: 'Remove access',
                onClick: onRemoveAccessBulk,
              },
            ]}
          />
        </FilteredTable>
      </Card>
    </div>
  )
}

const GroupCard = ({ group }) => {
  return (
    <Card className="mb-4">
      {group ? (
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold ">{group.groupName}</h2>
          {/* implement displayName on api */}
          <div className="flex flex-row flex-wrap">
            {/* <Tag property="Group ID" value={group.groupId} /> */}
            <Tag property="SID" value={group.sid} />
            {/* <Tag property="Tenant ID" value={group.tenantId} /> */}
            <Tag property="Tenant Name" value={group.tenantName} />
          </div>
        </div>
      ) : (
        <div className="flex">
          <Spinner className="my-auto mr-5" />
        </div>
      )}
    </Card>
  )
}

const tableHeaders = [
  {
    id: 'select',
    resizable: false,
    width: 40,
    Header: ({ getToggleAllRowsSelectedProps }) => (
      <div className="px-1">
        <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
      </div>
    ),
    Cell: (props) => {
      console.log(props)
      return (
        <div className="px-1">
          <IndeterminateCheckbox {...props.row.getToggleRowSelectedProps()} />
        </div>
      )
    },
  },
  // {
  //   Header: 'ID',
  //   i18n: 'indexObjects:indexObjectsTable.id',
  //   accessor: 'objectId',
  //   width: 70,
  // },
  {
    Header: 'Name',
    i18n: 'indexObjects:indexObjectsTable.name',
    accessor: 'name',
    width: 300,
    Cell: (props) => {
      return <ObjectName object={props.row.original} />
    },
  },
  {
    Header: 'External ID',
    i18n: 'indexObjects:indexObjectsTable.externalId',
    accessor: 'externalId',
    width: 150,
  },
  {
    Header: 'Active',
    accessor: 'active',
    width: 85,
    Cell: (props) => {
      return <ObjectActive object={props.row.original} />
    },
  },
  {
    Header: 'Public',
    accessor: 'isPublic',
    width: 85,
    Cell: (props) => {
      return <ObjectPublic object={props.row.original} />
    },
  },
  {
    Header: 'State',
    i18n: 'indexObjects:indexObjectsTable.state',
    accessor: 'state',
    width: 85,
    Cell: (props) => {
      return <ObjectOnlineState object={props.row.original} />
    },
  },
]

export default AdminGroupObjectsView
